import {
  AccountOwnerType,
  AccountTransaction,
  formatCurrency,
  PaymentMethodType,
  PersonRef,
  TransactionCategory,
  TransactionFlow,
} from '@bofrak-backend/shared';
import {
  ChevronDownIcon,
  DeleteIcon,
  TriangleDownIcon,
  TriangleUpIcon,
} from '@chakra-ui/icons';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Collapse,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  SimpleGrid,
  Skeleton,
  Spacer,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useBreakpointValue,
  useDisclosure,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import moment from 'moment';
import { useCallback, useMemo, useState } from 'react';
import { CiDeliveryTruck } from 'react-icons/ci';
import {
  FaComment,
  FaFilter,
  FaMoneyBillWave,
  FaSort,
  FaUser,
  FaUsers,
} from 'react-icons/fa';
import { FaArrowDownUpAcrossLine } from 'react-icons/fa6';
import { FiRotateCcw } from 'react-icons/fi';
import { GiReceiveMoney } from 'react-icons/gi';
import { HiOutlineReceiptRefund } from 'react-icons/hi';
import { IconType } from 'react-icons/lib';
import {
  MdOutlineAddCircle,
  MdOutlineAttachMoney,
  MdOutlineTrendingUp,
} from 'react-icons/md';
import { RiArrowLeftRightFill } from 'react-icons/ri';
import { TbReceipt } from 'react-icons/tb';
import { useRecoilValue } from 'recoil';

import { adminAtom } from '../recoil/atoms';
import { useReverseTransactionFlow } from './ReverseTransactionModal';
import { useSharedUiTranslation } from '../../i18n';
import {
  getPaymentMethodTypeLabel,
  getTransactionCategoryLabel,
  getTransactionFlowLabel,
} from './transaction-labels';

// Sorting types
export type SortField =
  | 'created_at'
  | 'amount'
  | 'flow'
  | 'category'
  | 'payment_type'
  | 'creator'
  | 'owner'
  | 'origin_description';
export type SortDirection = 'asc' | 'desc';

export const categoryIcons: Record<TransactionCategory, IconType> = {
  [TransactionCategory.TRANSFER_IN]: RiArrowLeftRightFill,
  [TransactionCategory.TRANSFER_OUT]: RiArrowLeftRightFill,
  [TransactionCategory.CLOSING_CORRECTION]: FaArrowDownUpAcrossLine,
  [TransactionCategory.CORRECTION]: FaArrowDownUpAcrossLine,
  [TransactionCategory.OPENING_CORRECTION]: FaArrowDownUpAcrossLine,
  [TransactionCategory.DEBT_PAYMENT]: GiReceiveMoney,
  [TransactionCategory.LOAN_PAYMENT]: GiReceiveMoney,
  [TransactionCategory.DIVIDEND]: MdOutlineAddCircle,
  [TransactionCategory.INVESTMENT]: MdOutlineTrendingUp,
  [TransactionCategory.LOAN_GIVEN]: FaMoneyBillWave,
  [TransactionCategory.SALES_RECEIPT]: TbReceipt,
  [TransactionCategory.SALES_REFUND]: HiOutlineReceiptRefund,
  [TransactionCategory.SUPPLIER_PAYMENT]: CiDeliveryTruck,
  [TransactionCategory.SUPPLIER_REFUND]: CiDeliveryTruck,
  [TransactionCategory.EXPENSE]: MdOutlineAttachMoney,
  [TransactionCategory.DEBT_PAYMENT_REVERSAL]: FiRotateCcw,
  [TransactionCategory.LOAN_PAYMENT_REVERSAL]: FiRotateCcw,
  [TransactionCategory.LOAN_GIVEN_REVERSAL]: FiRotateCcw,
  [TransactionCategory.EXPENSE_REVERSAL]: FiRotateCcw,
  [TransactionCategory.DIVIDEND_REVERSAL]: FiRotateCcw,
  [TransactionCategory.INVESTMENT_REVERSAL]: FiRotateCcw,
};

// Categories that support reversal (each has a matching *_REVERSAL counterpart).
export const REVERSIBLE_CATEGORIES: ReadonlySet<TransactionCategory> = new Set([
  TransactionCategory.DEBT_PAYMENT,
  TransactionCategory.LOAN_PAYMENT,
  TransactionCategory.LOAN_GIVEN,
  TransactionCategory.EXPENSE,
  TransactionCategory.DIVIDEND,
  TransactionCategory.INVESTMENT,
  // Routed through the purchase-order payments API (see ReverseTransactionModal).
  // The resulting SUPPLIER_REFUND is tagged via `reverses_transaction_id` rather
  // than a dedicated *_REVERSAL category, so partial PO refunds stay neutral.
  TransactionCategory.SUPPLIER_PAYMENT,
]);

// Reversal categories (the *_REVERSAL counterparts).
export const REVERSAL_CATEGORIES: ReadonlySet<TransactionCategory> = new Set([
  TransactionCategory.DEBT_PAYMENT_REVERSAL,
  TransactionCategory.LOAN_PAYMENT_REVERSAL,
  TransactionCategory.LOAN_GIVEN_REVERSAL,
  TransactionCategory.EXPENSE_REVERSAL,
  TransactionCategory.DIVIDEND_REVERSAL,
  TransactionCategory.INVESTMENT_REVERSAL,
]);

export const isReversalTransaction = (t: AccountTransaction): boolean =>
  REVERSAL_CATEGORIES.has(t.category) || !!t.reverses_transaction_id;

export const isReversedTransaction = (t: AccountTransaction): boolean =>
  !!t.reversed_by_transaction_id;

export const isReversibleTransaction = (t: AccountTransaction): boolean =>
  REVERSIBLE_CATEGORIES.has(t.category) &&
  !isReversalTransaction(t) &&
  !isReversedTransaction(t);

// Original → reversal category pairs. SUPPLIER_PAYMENT is special-cased: its
// ledger reversal is a SUPPLIER_REFUND tagged via `reverses_transaction_id`
// (no dedicated *_REVERSAL category), see ReverseTransactionModal.
const REVERSAL_CATEGORY_PAIRS: ReadonlyArray<
  readonly [TransactionCategory, TransactionCategory]
> = [
  [TransactionCategory.DEBT_PAYMENT, TransactionCategory.DEBT_PAYMENT_REVERSAL],
  [TransactionCategory.LOAN_PAYMENT, TransactionCategory.LOAN_PAYMENT_REVERSAL],
  [TransactionCategory.LOAN_GIVEN, TransactionCategory.LOAN_GIVEN_REVERSAL],
  [TransactionCategory.EXPENSE, TransactionCategory.EXPENSE_REVERSAL],
  [TransactionCategory.DIVIDEND, TransactionCategory.DIVIDEND_REVERSAL],
  [TransactionCategory.INVESTMENT, TransactionCategory.INVESTMENT_REVERSAL],
  [TransactionCategory.SUPPLIER_PAYMENT, TransactionCategory.SUPPLIER_REFUND],
];

/** True when `a` and `b` are the original/reversal category counterparts. */
export const categoriesAreLinked = (
  a: TransactionCategory,
  b: TransactionCategory,
): boolean => {
  if (a === b) return false;
  return REVERSAL_CATEGORY_PAIRS.some(
    ([original, reversal]) =>
      (a === original && b === reversal) || (a === reversal && b === original),
  );
};

/**
 * A candidate is only a valid partner when it belongs to the item's
 * original/reversal category pair AND mirrors the same absolute amount.
 * This rejects false positives such as an EXPENSE being linked to a
 * sales receipt that happens to share the composite key.
 */
export const isReversalPartner = (
  transaction: AccountTransaction,
  partner: AccountTransaction,
): boolean => {
  if (Math.abs(transaction.amount) !== Math.abs(partner.amount)) return false;
  return categoriesAreLinked(transaction.category, partner.category);
};

// --------------------------------------------------------------------------
// Reversal pairing
//
// A reversal transaction points at its original via `reverses_transaction_id`
// (composite `account_id#version`) and the original is stamped with
// `reversed_by_transaction_id` once the reversal is written. Pair them
// visually so the user sees the link regardless of when each happened.
// --------------------------------------------------------------------------

/**
 * Raw composite used in reversal stamps (`account_id#version`). NOT globally
 * unique: version only increments per payment scope.
 */
const txComposite = (t: AccountTransaction): string =>
  `${t.account_id}#${t.version}`;

/**
 * Scope-aware identity key. A reversal always mirrors its original's store
 * and payment scope, so including it makes the key unique even when two
 * transactions share the same `account_id#version`.
 */
const txScopedKey = (t: AccountTransaction): string =>
  `${t.account_id}#${t.store_id}#${t.payment_type}#${t.payment_method_id}#${t.version}`;

/**
 * Scoped key for the target of a reversal stamp. The stamp only carries
 * `account_id#version`, so the linking item's own store/payment scope is
 * inserted BETWEEN them to match the `txScopedKey` layout — the partner is
 * guaranteed to share that scope.
 */
const txScopedKeyForLink = (t: AccountTransaction, link: string): string => {
  const separator = link.lastIndexOf('#');
  const accountId = separator === -1 ? link : link.slice(0, separator);
  const version = separator === -1 ? '' : link.slice(separator + 1);
  return `${accountId}#${t.store_id}#${t.payment_type}#${t.payment_method_id}#${version}`;
};

export type ReversalRelationship = 'reverses' | 'reversed_by';

/**
 * Lookup of every transaction by its scope-aware identity key. The full
 * unfiltered `transactions` take precedence; `reversals` only fill gaps.
 * Must never be built from filtered items — filtering must not prevent a
 * visible item from resolving its partner.
 */
export function buildPartnerLookup(
  transactions: AccountTransaction[] = [],
  reversals: AccountTransaction[] = [],
): Map<string, AccountTransaction> {
  const byKey = new Map<string, AccountTransaction>();
  transactions.forEach((t) => byKey.set(txScopedKey(t), t));
  reversals.forEach((t) => {
    if (!byKey.has(txScopedKey(t))) byKey.set(txScopedKey(t), t);
  });
  return byKey;
}

/**
 * Resolves the partner for a single transaction via the composite
 * `account_id#version` stamps. The stamp target is looked up with the
 * linking item's OWN payment scope appended (a reversal always mirrors its
 * original's store/payment scope), so equal composites on different payment
 * methods can never collide. Read-only: a partner found only in `reversals`
 * never becomes a top-level item.
 */
export function getTransactionPartner(
  transaction: AccountTransaction,
  byKey: ReadonlyMap<string, AccountTransaction>,
): AccountTransaction | undefined {
  const partnerKey =
    transaction.reversed_by_transaction_id ??
    transaction.reverses_transaction_id;
  if (!partnerKey) return undefined;
  const partner = byKey.get(txScopedKeyForLink(transaction, partnerKey));
  return partner &&
    txScopedKey(partner) !== txScopedKey(transaction) &&
    isReversalPartner(transaction, partner)
    ? partner
    : undefined;
}

/**
 * Describes the partner relative to the current item:
 * - 'reverses':    the partner REVERSES the current item (item = original).
 * - 'reversed_by': the partner was REVERSED BY the current item (item = reversal).
 */
export function getReversalRelationship(
  transaction: AccountTransaction,
  partner: AccountTransaction,
): ReversalRelationship {
  return transaction.reversed_by_transaction_id === txComposite(partner)
    ? 'reverses'
    : 'reversed_by';
}

/** Immutable fallback used when no partner lookup is provided. */
const EMPTY_PARTNER_LOOKUP: ReadonlyMap<string, AccountTransaction> = new Map();

/**
 * Merges partner candidates from the API `reversals` field with any
 * transactions that carry a reversal link, deduplicated by their
 * scope-aware identity. Use this when a list is split into groups (e.g. by
 * category or employee) so each table can still pair a row with its partner
 * even when the partner lives in another group. Unpaired candidates never
 * render as standalone rows.
 */
export function mergeReversalCandidates(
  extras: AccountTransaction[] = [],
  linkedTransactions: AccountTransaction[] = [],
): AccountTransaction[] {
  const seen = new Set<string>();
  const merged: AccountTransaction[] = [];
  for (const t of extras) {
    const key = txScopedKey(t);
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(t);
  }
  for (const t of linkedTransactions) {
    if (!t.reverses_transaction_id && !t.reversed_by_transaction_id) continue;
    const key = txScopedKey(t);
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(t);
  }
  return merged;
}

/** Accent colour used to visually bind a reversal to its original. */
export const PAIR_LINK_COLOR = 'purple.400';

/** Background tint applied to a linked item's block. */
export const PAIR_GROUP_BG = 'purple.50';
export const PAIR_GROUP_BG_DARK = 'purple.900';

export interface SortConfig {
  field: SortField;
  direction: SortDirection;
}

// Filter types
export interface TransactionFilters {
  flows: TransactionFlow[];
  categories: TransactionCategory[];
  paymentTypes: PaymentMethodType[];
  owners: PersonRef[];
  creators: PersonRef[];
  involvedParties: PersonRef[];
}

export const emptyFilters: TransactionFilters = {
  flows: [],
  categories: [],
  paymentTypes: [],
  owners: [],
  creators: [],
  involvedParties: [],
};

interface TransactionCardListProps {
  transactions: AccountTransaction[];
  onTransactionClick?: (transaction: AccountTransaction) => void;
  getRowBgColor: (isInflow: boolean) => string | undefined;
  getRowHoverBgColor: (isInflow: boolean) => string | undefined;
  isCompact?: boolean;
  onReverseTransaction?: (transaction: AccountTransaction) => void;
  partnerLookup?: ReadonlyMap<string, AccountTransaction>;
}

const TransactionCardList: React.FC<TransactionCardListProps> = ({
  transactions,
  onTransactionClick,
  getRowBgColor,
  getRowHoverBgColor,
  isCompact = false,
  onReverseTransaction,
  partnerLookup,
}) => {
  const { t } = useSharedUiTranslation();
  const renderBody = (
    transaction: AccountTransaction,
    opts: {
      asPartner?: boolean;
      relationship?: ReversalRelationship;
      standalone?: boolean;
    } = {},
  ) => {
    const asPartner = opts.asPartner === true;
    const isInflow = transaction.flow === 'INFLOW';
    const isReversal = isReversalTransaction(transaction);
    const isReversed = isReversedTransaction(transaction);
    // The item itself keeps its natural styling; only the contextual partner
    // (lower hierarchy inside the purple block) is muted. Standalone rows
    // keep the historical muting for reversals/cancelled originals.
    const isMuted =
      asPartner || (opts.standalone === true && (isReversal || isReversed));
    const canReverse =
      !!onReverseTransaction &&
      isReversibleTransaction(transaction) &&
      !asPartner;
    const mutedTextProps = isMuted ? { color: 'gray.500' } : {};
    return (
      <Box
        p={isCompact ? 1.5 : 2}
        pl={asPartner ? (isCompact ? 7 : 10) : undefined}
        opacity={asPartner ? 0.9 : 1}
        cursor={onTransactionClick ? 'pointer' : 'default'}
        onClick={(e) => {
          e.stopPropagation();
          onTransactionClick?.(transaction);
        }}>
        <HStack spacing={0}>
          <Icon
            mr={isCompact ? 2 : 5}
            as={categoryIcons[transaction.category]}
            boxSize={asPartner ? 3 : isCompact ? 4 : 6}
            color={isMuted ? 'gray.400' : isInflow ? 'green.500' : 'red.500'}
          />
          <VStack align="start" spacing={0} flex={1}>
            <HStack spacing={1} flexWrap="wrap">
              <Text
                fontSize={asPartner ? '2xs' : isCompact ? 'xx-small' : 'xs'}
                fontWeight={'bold'}
                fontStyle={asPartner ? 'italic' : undefined}
                {...mutedTextProps}>
                {getTransactionCategoryLabel(transaction.category)}
              </Text>
            </HStack>
            <Text
              fontSize={asPartner ? '2xs' : 'x-small'}
              fontStyle={asPartner ? 'italic' : undefined}
              {...mutedTextProps}>
              {moment(transaction.created_at).format('DD.MM.YY @ HH:mm')}
              {isCompact
                ? ''
                : ` - ${transaction.creator?.name || transaction.owner?.name || t('Unknown')}`}
            </Text>
          </VStack>
          <Spacer />
          <VStack align="end" spacing={0} flex={1}>
            <Text
              fontSize={asPartner ? '2xs' : isCompact ? 'xs' : 'md'}
              fontWeight={asPartner ? 'normal' : 'bold'}
              fontStyle={asPartner ? 'italic' : undefined}
              textDecoration={
                isReversed && !isReversal ? 'line-through' : undefined
              }
              {...mutedTextProps}>
              {formatCurrency(transaction.amount, true)}
            </Text>
            <Text
              fontSize={asPartner ? '2xs' : isCompact ? 'xx-small' : 'xs'}
              fontStyle={asPartner ? 'italic' : undefined}
              color={
                asPartner ? 'gray.400' : isMuted ? 'gray.500' : 'gray.600'
              }>
              {getPaymentMethodTypeLabel(transaction.payment_type)}
            </Text>
          </VStack>
          {canReverse && (
            <Tooltip label={t('Delete Transaction')} placement="top">
              <IconButton
                aria-label={t('Delete Transaction')}
                icon={<Icon as={DeleteIcon} />}
                size={isCompact ? 'xs' : 'sm'}
                variant="ghost"
                colorScheme="orange"
                ml={2}
                onClick={(e) => {
                  e.stopPropagation();
                  onReverseTransaction?.(transaction);
                }}
              />
            </Tooltip>
          )}
        </HStack>
        {!isCompact && transaction.origin_entity?.description && (
          <Text
            fontSize="x-small"
            color={isMuted ? 'gray.500' : 'gray.600'}
            mt={1}
            pl={11}>
            {transaction.origin_entity.description}
          </Text>
        )}
      </Box>
    );
  };

  return (
    <VStack spacing={isCompact ? 1 : 3} align="stretch">
      {transactions.map((transaction) => {
        const partner = getTransactionPartner(
          transaction,
          partnerLookup ?? EMPTY_PARTNER_LOOKUP,
        );
        const relationship = partner
          ? getReversalRelationship(transaction, partner)
          : undefined;
        const isInflow = transaction.flow === 'INFLOW';

        if (!partner || !relationship) {
          return (
            <Box
              key={txScopedKey(transaction)}
              borderRadius={isCompact ? 'sm' : 'md'}
              bg={getRowBgColor(isInflow)}
              opacity={
                isReversalTransaction(transaction) ||
                isReversedTransaction(transaction)
                  ? 0.7
                  : 1
              }
              _hover={{ bg: getRowHoverBgColor(isInflow) }}
              transition="background 0.2s">
              {renderBody(transaction, { standalone: true })}
            </Box>
          );
        }

        // One purple block == ONE item from `transactions`. The partner is
        // contextual information rendered at a lower hierarchy — it is not an
        // additional row and never changes the rendered item count.
        return (
          <Box
            key={txScopedKey(transaction)}
            borderRadius={isCompact ? 'sm' : 'md'}
            bg={PAIR_GROUP_BG}
            transition="background 0.2s">
            {renderBody(transaction)}
            <Box
              mx={2}
              borderTopWidth="1px"
              borderTopStyle="dashed"
              borderTopColor="purple.300"
            />
            {renderBody(partner, { asPartner: true, relationship })}
          </Box>
        );
      })}
    </VStack>
  );
};

// Filter Component
interface TransactionFilterProps {
  transactions: AccountTransaction[];
  filters: TransactionFilters;
  onFiltersChange: (filters: TransactionFilters) => void;
  disableOwnerFilter?: boolean;
  selectedOwnerId?: string;
}

const TransactionFilterPanel: React.FC<TransactionFilterProps> = ({
  transactions,
  filters,
  onFiltersChange,
  disableOwnerFilter = false,
  selectedOwnerId,
}) => {
  const { isOpen, onToggle } = useDisclosure();
  const { t } = useSharedUiTranslation();

  // Extract unique values from transactions for filter options
  const filterOptions = useMemo(() => {
    const flows = new Set<TransactionFlow>();
    const categories = new Set<TransactionCategory>();
    const paymentTypes = new Set<PaymentMethodType>();
    const owners = new Map<string, PersonRef>();
    const creators = new Map<string, PersonRef>();
    const involvedParties = new Map<string, PersonRef>();

    transactions.forEach((t) => {
      flows.add(t.flow);
      categories.add(t.category);
      paymentTypes.add(t.payment_type);

      if (t.owner && (!disableOwnerFilter || t.owner.id !== selectedOwnerId)) {
        owners.set(t.owner.id, t.owner);
      }
      if (t.creator) {
        creators.set(t.creator.id, t.creator);
      }
      if (t.involved_parties) {
        t.involved_parties.forEach((p) => {
          involvedParties.set(p.id, p);
        });
      }
    });

    return {
      flows: Array.from(flows),
      categories: Array.from(categories),
      paymentTypes: Array.from(paymentTypes),
      owners: Array.from(owners.values()),
      creators: Array.from(creators.values()),
      involvedParties: Array.from(involvedParties.values()),
    };
  }, [transactions, disableOwnerFilter, selectedOwnerId]);

  const hasActiveFilters = useMemo(() => {
    return (
      filters.flows.length > 0 ||
      filters.categories.length > 0 ||
      filters.paymentTypes.length > 0 ||
      filters.owners.length > 0 ||
      filters.creators.length > 0 ||
      filters.involvedParties.length > 0
    );
  }, [filters]);

  const activeFilterCount = useMemo(() => {
    return (
      filters.flows.length +
      filters.categories.length +
      filters.paymentTypes.length +
      filters.owners.length +
      filters.creators.length +
      filters.involvedParties.length
    );
  }, [filters]);

  const handleClearFilters = () => {
    onFiltersChange(emptyFilters);
  };

  return (
    <Box mb={3}>
      <HStack justify="space-between" mb={2}>
        <Button
          leftIcon={<Icon as={FaFilter} />}
          rightIcon={<ChevronDownIcon />}
          size="sm"
          variant={hasActiveFilters ? 'solid' : 'outline'}
          colorScheme={hasActiveFilters ? 'blue' : 'gray'}
          onClick={onToggle}>
          {t('Filters')}
          {activeFilterCount > 0 && (
            <Badge ml={2} colorScheme="red" borderRadius="full">
              {activeFilterCount}
            </Badge>
          )}
        </Button>
        {hasActiveFilters && (
          <Button size="xs" variant="ghost" onClick={handleClearFilters}>
            {t('Clear all')}
          </Button>
        )}
      </HStack>

      <Collapse in={isOpen} animateOpacity>
        <Box
          p={3}
          bg="gray.50"
          borderRadius="md"
          borderWidth="1px"
          borderColor="gray.200">
          <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={3}>
            {/* Flow Filter */}
            <Box>
              <Menu closeOnSelect={false}>
                <MenuButton
                  as={Button}
                  size="sm"
                  variant="outline"
                  rightIcon={<ChevronDownIcon />}
                  w="full"
                  textAlign="left">
                  <HStack justify="space-between" w="full">
                    <Text fontSize="xs">{t('Flow')}</Text>
                    {filters.flows.length > 0 && (
                      <Badge colorScheme="blue" fontSize="2xs">
                        {filters.flows.length}
                      </Badge>
                    )}
                  </HStack>
                </MenuButton>
                <MenuList minWidth="200px">
                  <MenuOptionGroup
                    type="checkbox"
                    value={filters.flows}
                    onChange={(values) =>
                      onFiltersChange({
                        ...filters,
                        flows: values as TransactionFlow[],
                      })
                    }>
                    {filterOptions.flows.map((flow) => (
                      <MenuItemOption key={flow} value={flow} fontSize="sm">
                        <Badge
                          colorScheme={flow === 'INFLOW' ? 'green' : 'red'}
                          mr={2}>
                          {flow === 'INFLOW' ? t('IN') : t('OUT')}
                        </Badge>
                        {getTransactionFlowLabel(flow)}
                      </MenuItemOption>
                    ))}
                  </MenuOptionGroup>
                </MenuList>
              </Menu>
            </Box>

            {/* Category Filter */}
            <Box>
              <Menu closeOnSelect={false}>
                <MenuButton
                  as={Button}
                  size="sm"
                  variant="outline"
                  rightIcon={<ChevronDownIcon />}
                  w="full"
                  textAlign="left">
                  <HStack justify="space-between" w="full">
                    <Text fontSize="xs">{t('Category')}</Text>
                    {filters.categories.length > 0 && (
                      <Badge colorScheme="blue" fontSize="2xs">
                        {filters.categories.length}
                      </Badge>
                    )}
                  </HStack>
                </MenuButton>
                <MenuList minWidth="200px" maxH="300px" overflowY="auto">
                  <MenuOptionGroup
                    type="checkbox"
                    value={filters.categories}
                    onChange={(values) =>
                      onFiltersChange({
                        ...filters,
                        categories: values as TransactionCategory[],
                      })
                    }>
                    {filterOptions.categories.map((category) => (
                      <MenuItemOption
                        key={category}
                        value={category}
                        fontSize="sm">
                        {getTransactionCategoryLabel(category)}
                      </MenuItemOption>
                    ))}
                  </MenuOptionGroup>
                </MenuList>
              </Menu>
            </Box>

            {/* Payment Type Filter */}
            <Box>
              <Menu closeOnSelect={false}>
                <MenuButton
                  as={Button}
                  size="sm"
                  variant="outline"
                  rightIcon={<ChevronDownIcon />}
                  w="full"
                  textAlign="left">
                  <HStack justify="space-between" w="full">
                    <Text fontSize="xs">{t('Payment Type')}</Text>
                    {filters.paymentTypes.length > 0 && (
                      <Badge colorScheme="blue" fontSize="2xs">
                        {filters.paymentTypes.length}
                      </Badge>
                    )}
                  </HStack>
                </MenuButton>
                <MenuList minWidth="200px" maxH="300px" overflowY="auto">
                  <MenuOptionGroup
                    type="checkbox"
                    value={filters.paymentTypes}
                    onChange={(values) =>
                      onFiltersChange({
                        ...filters,
                        paymentTypes: values as PaymentMethodType[],
                      })
                    }>
                    {filterOptions.paymentTypes.map((type) => (
                      <MenuItemOption key={type} value={type} fontSize="sm">
                        {getPaymentMethodTypeLabel(type)}
                      </MenuItemOption>
                    ))}
                  </MenuOptionGroup>
                </MenuList>
              </Menu>
            </Box>

            {/* Owner Filter - Hidden when disableOwnerFilter is true */}
            {!disableOwnerFilter && filterOptions.owners.length > 0 && (
              <Box>
                <Menu closeOnSelect={false}>
                  <MenuButton
                    as={Button}
                    size="sm"
                    variant="outline"
                    rightIcon={<ChevronDownIcon />}
                    w="full"
                    textAlign="left">
                    <HStack justify="space-between" w="full">
                      <Text fontSize="xs">{t('Owner')}</Text>
                      {filters.owners.length > 0 && (
                        <Badge colorScheme="blue" fontSize="2xs">
                          {filters.owners.length}
                        </Badge>
                      )}
                    </HStack>
                  </MenuButton>
                  <MenuList minWidth="200px" maxH="300px" overflowY="auto">
                    <MenuOptionGroup
                      type="checkbox"
                      value={filters.owners.map((o) => o.id)}
                      onChange={(values) =>
                        onFiltersChange({
                          ...filters,
                          owners: filterOptions.owners.filter((owner) =>
                            (values as string[]).includes(owner.id),
                          ),
                        })
                      }>
                      {filterOptions.owners.map((owner) => (
                        <MenuItemOption
                          key={owner.id}
                          value={owner.id}
                          fontSize="sm">
                          <Icon as={FaUser} mr={2} color="gray.500" />
                          {owner.name || owner.id}
                        </MenuItemOption>
                      ))}
                    </MenuOptionGroup>
                  </MenuList>
                </Menu>
              </Box>
            )}

            {/* Creator Filter */}
            {filterOptions.creators.length > 0 && (
              <Box>
                <Menu closeOnSelect={false}>
                  <MenuButton
                    as={Button}
                    size="sm"
                    variant="outline"
                    rightIcon={<ChevronDownIcon />}
                    w="full"
                    textAlign="left">
                    <HStack justify="space-between" w="full">
                      <Text fontSize="xs">{t('Creator')}</Text>
                      {filters.creators.length > 0 && (
                        <Badge colorScheme="blue" fontSize="2xs">
                          {filters.creators.length}
                        </Badge>
                      )}
                    </HStack>
                  </MenuButton>
                  <MenuList minWidth="200px" maxH="300px" overflowY="auto">
                    <MenuOptionGroup
                      type="checkbox"
                      value={filters.creators.map((c) => c.id)}
                      onChange={(values) =>
                        onFiltersChange({
                          ...filters,
                          creators: filterOptions.creators.filter((creator) =>
                            (values as string[]).includes(creator.id),
                          ),
                        })
                      }>
                      {filterOptions.creators.map((creator) => (
                        <MenuItemOption
                          key={creator.id}
                          value={creator.id}
                          fontSize="sm">
                          <Icon as={FaUser} mr={2} color="gray.500" />
                          {creator.name || creator.id}
                        </MenuItemOption>
                      ))}
                    </MenuOptionGroup>
                  </MenuList>
                </Menu>
              </Box>
            )}

            {/* Involved Parties Filter */}
            {filterOptions.involvedParties.length > 0 && (
              <Box>
                <Menu closeOnSelect={false}>
                  <MenuButton
                    as={Button}
                    size="sm"
                    variant="outline"
                    rightIcon={<ChevronDownIcon />}
                    w="full"
                    textAlign="left">
                    <HStack justify="space-between" w="full">
                      <Text fontSize="xs">{t('Involved Parties')}</Text>
                      {filters.involvedParties.length > 0 && (
                        <Badge colorScheme="blue" fontSize="2xs">
                          {filters.involvedParties.length}
                        </Badge>
                      )}
                    </HStack>
                  </MenuButton>
                  <MenuList minWidth="200px" maxH="300px" overflowY="auto">
                    <MenuOptionGroup
                      type="checkbox"
                      value={filters.involvedParties.map((p) => p.id)}
                      onChange={(values) =>
                        onFiltersChange({
                          ...filters,
                          involvedParties: filterOptions.involvedParties.filter(
                            (party) => (values as string[]).includes(party.id),
                          ),
                        })
                      }>
                      {filterOptions.involvedParties.map((party) => (
                        <MenuItemOption
                          key={party.id}
                          value={party.id}
                          fontSize="sm">
                          <Icon as={FaUsers} mr={2} color="gray.500" />
                          {party.name || party.id}
                        </MenuItemOption>
                      ))}
                    </MenuOptionGroup>
                  </MenuList>
                </Menu>
              </Box>
            )}
          </SimpleGrid>

          {/* Active Filters Display */}
          {hasActiveFilters && (
            <Box mt={3} pt={3} borderTopWidth="1px" borderColor="gray.200">
              <Text fontSize="xs" color="gray.500" mb={2}>
                {t('Active filters:')}
              </Text>
              <Wrap spacing={1}>
                {filters.flows.map((flow) => (
                  <WrapItem key={`flow-${flow}`}>
                    <Badge
                      colorScheme="blue"
                      variant="subtle"
                      cursor="pointer"
                      onClick={() =>
                        onFiltersChange({
                          ...filters,
                          flows: filters.flows.filter((f) => f !== flow),
                        })
                      }>
                      {t('Flow: {{flow}}', {
                        flow: getTransactionFlowLabel(flow),
                      })}{' '}
                      ✕
                    </Badge>
                  </WrapItem>
                ))}
                {filters.categories.map((cat) => (
                  <WrapItem key={`cat-${cat}`}>
                    <Badge
                      colorScheme="green"
                      variant="subtle"
                      cursor="pointer"
                      onClick={() =>
                        onFiltersChange({
                          ...filters,
                          categories: filters.categories.filter(
                            (c) => c !== cat,
                          ),
                        })
                      }>
                      {getTransactionCategoryLabel(cat)} ✕
                    </Badge>
                  </WrapItem>
                ))}
                {filters.paymentTypes.map((pt) => (
                  <WrapItem key={`pt-${pt}`}>
                    <Badge
                      colorScheme="purple"
                      variant="subtle"
                      cursor="pointer"
                      onClick={() =>
                        onFiltersChange({
                          ...filters,
                          paymentTypes: filters.paymentTypes.filter(
                            (p) => p !== pt,
                          ),
                        })
                      }>
                      {getPaymentMethodTypeLabel(pt)} ✕
                    </Badge>
                  </WrapItem>
                ))}
                {filters.owners.map((owner) => (
                  <WrapItem key={`owner-${owner.id}`}>
                    <Badge
                      colorScheme="orange"
                      variant="subtle"
                      cursor="pointer"
                      onClick={() =>
                        onFiltersChange({
                          ...filters,
                          owners: filters.owners.filter(
                            (o) => o.id !== owner.id,
                          ),
                        })
                      }>
                      {t('Owner: {{name}}', { name: owner.name })} ✕
                    </Badge>
                  </WrapItem>
                ))}
                {filters.creators.map((creator) => (
                  <WrapItem key={`creator-${creator.id}`}>
                    <Badge
                      colorScheme="cyan"
                      variant="subtle"
                      cursor="pointer"
                      onClick={() =>
                        onFiltersChange({
                          ...filters,
                          creators: filters.creators.filter(
                            (c) => c.id !== creator.id,
                          ),
                        })
                      }>
                      {t('Creator: {{name}}', { name: creator.name })} ✕
                    </Badge>
                  </WrapItem>
                ))}
                {filters.involvedParties.map((party) => (
                  <WrapItem key={`party-${party.id}`}>
                    <Badge
                      colorScheme="teal"
                      variant="subtle"
                      cursor="pointer"
                      onClick={() =>
                        onFiltersChange({
                          ...filters,
                          involvedParties: filters.involvedParties.filter(
                            (p) => p.id !== party.id,
                          ),
                        })
                      }>
                      {t('Involved: {{id}}', { id: party.id.slice(0, 8) })} ✕
                    </Badge>
                  </WrapItem>
                ))}
              </Wrap>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

// Helper function to filter transactions
const filterTransactions = (
  transactions: AccountTransaction[],
  filters: TransactionFilters,
): AccountTransaction[] => {
  return transactions.filter((t) => {
    // Flow filter
    if (filters.flows.length > 0 && !filters.flows.includes(t.flow)) {
      return false;
    }
    // Category filter
    if (
      filters.categories.length > 0 &&
      !filters.categories.includes(t.category)
    ) {
      return false;
    }
    // Payment type filter
    if (
      filters.paymentTypes.length > 0 &&
      !filters.paymentTypes.includes(t.payment_type)
    ) {
      return false;
    }
    // Owner filter
    if (
      filters.owners.length > 0 &&
      (!t.owner ||
        !filters.owners
          .map((o) => o.id.toLowerCase())
          .includes(t.owner.id.toLowerCase()))
    ) {
      return false;
    }
    // Creator filter
    if (
      filters.creators.length > 0 &&
      (!t.creator ||
        !filters.creators
          .map((c) => c.id.toLowerCase())
          .includes(t.creator.id.toLowerCase()))
    ) {
      return false;
    }
    // Involved parties filter
    if (filters.involvedParties.length > 0) {
      const involvedIds = (t.involved_parties || []).map((p) => p.id);
      if (
        !filters.involvedParties.some((party) => involvedIds.includes(party.id))
      ) {
        return false;
      }
    }
    return true;
  });
};

export interface ResponsiveTransactionTableProps {
  transactions: AccountTransaction[];
  /**
   * Partner transactions returned by the accounts API (`reversals` field of
   * `AccountTransactionChangeSummary`). Used only to complete reversal pairs
   * whose counterpart is not present in `transactions`; unpaired entries are
   * ignored.
   */
  reversals?: AccountTransaction[];
  onTransactionClick?: (transaction: AccountTransaction) => void;
  isLoading?: boolean;
  disableOwnerFilter?: boolean;
  selectedOwnerId?: string;
  /** Enable flow-based row background colors (green for inflows, red for outflows) */
  showFlowBackgroundColors?: boolean;
  /** Maximum height for the table container (e.g., "300px", "50vh") */
  maxHeight?: string;
  tableView?: boolean;
  /** Hide the filter panel toggle and filter UI. Defaults to true. */
  showFilters?: boolean;
  /** Use compact padding and font sizes for embedded usage. Defaults to false. */
  isCompact?: boolean;
  /** When provided, shows a Reverse button on each eligible transaction row. */
  onReverseTransaction?: (transaction: AccountTransaction) => void;
  /**
   * When provided, the table renders an internal reverse-transaction modal
   * and automatically wires it to the Reverse button on each eligible row.
   * Prefer this over `onReverseTransaction` for a zero-boilerplate flow.
   */
  reverseConfig?: {
    creator?: PersonRef | null;
    shift_id?: string;
    onReversed?: (reversal: AccountTransaction | null) => void;
  };
}

// Responsive Transaction Table Component
export const ResponsiveTransactionTable: React.FC<
  ResponsiveTransactionTableProps
> = ({
  transactions,
  reversals,
  onTransactionClick,
  isLoading,
  disableOwnerFilter = false,
  selectedOwnerId,
  showFlowBackgroundColors = false,
  maxHeight,
  tableView = false,
  showFilters = true,
  isCompact = false,
  onReverseTransaction,
  reverseConfig,
}) => {
  // Auto-derive a reverse creator from the shared-ui admin atom so consumers
  // don't have to wire merchant/store/admin plumbing at every call site.
  const admin = useRecoilValue(adminAtom);
  const { t } = useSharedUiTranslation();
  const autoCreator: PersonRef | null = admin
    ? {
        id: admin.id,
        type: AccountOwnerType.USER,
        name: admin.name,
      }
    : null;
  const resolvedReverseConfig = reverseConfig ?? { creator: autoCreator };
  const {
    onReverseTransaction: internalOnReverse,
    reverseModal: internalReverseModal,
  } = useReverseTransactionFlow({
    creator: resolvedReverseConfig.creator,
    shift_id: resolvedReverseConfig.shift_id,
    onReversed: resolvedReverseConfig.onReversed,
  });
  const reverseEnabled =
    !!onReverseTransaction ||
    (resolvedReverseConfig.creator !== null &&
      resolvedReverseConfig.creator !== undefined);
  const effectiveReverseHandler = onReverseTransaction
    ? onReverseTransaction
    : reverseEnabled
      ? internalOnReverse
      : undefined;
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'created_at',
    direction: 'desc',
  });
  const [filters, setFilters] = useState<TransactionFilters>(emptyFilters);

  const handleSort = useCallback((field: SortField) => {
    setSortConfig((prev) => ({
      field,
      direction:
        prev.field === field && prev.direction === 'desc' ? 'asc' : 'desc',
    }));
  }, []);

  // Filter and sort transactions
  const filteredTransactions = useMemo(() => {
    return filterTransactions(transactions, filters);
  }, [transactions, filters]);

  const sortedTransactions = useMemo(() => {
    if (!filteredTransactions || filteredTransactions.length === 0) return [];

    return [...filteredTransactions].sort((a, b) => {
      const { field, direction } = sortConfig;
      let comparison = 0;

      switch (field) {
        case 'created_at':
          comparison =
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
        case 'amount':
          comparison = a.amount - b.amount;
          break;
        case 'flow':
          comparison = a.flow.localeCompare(b.flow);
          break;
        case 'category':
          comparison = a.category.localeCompare(b.category);
          break;
        case 'payment_type':
          comparison = a.payment_type.localeCompare(b.payment_type);
          break;
        case 'creator':
          const creatorA = a.creator?.name || a.creator?.id || '';
          const creatorB = b.creator?.name || b.creator?.id || '';
          comparison = creatorA.localeCompare(creatorB);
          break;
        case 'owner':
          const ownerA = a.owner?.name || a.owner?.id || '';
          const ownerB = b.owner?.name || b.owner?.id || '';
          comparison = ownerA.localeCompare(ownerB);
          break;
        case 'origin_description':
          const originA = a.origin_entity?.description || '';
          const originB = b.origin_entity?.description || '';
          comparison = originA.localeCompare(originB);
          break;
        default:
          comparison = 0;
      }

      return direction === 'asc' ? comparison : -comparison;
    });
  }, [filteredTransactions, sortConfig]);

  // Authoritative partner lookup built from the FULL items list + reversals.
  // Filtering must not affect it: a visible item must always be able to
  // resolve its partner. `sortedTransactions` remains the only top-level
  // render list — partners never become additional rows.
  const partnerLookup = useMemo(
    () => buildPartnerLookup(transactions, reversals),
    [transactions, reversals],
  );

  const getSortIcon = (field: SortField) => {
    if (sortConfig.field !== field) {
      return <Icon as={FaSort} boxSize={3} color="gray.400" />;
    }
    return sortConfig.direction === 'asc' ? (
      <TriangleUpIcon boxSize={3} color="blue.500" />
    ) : (
      <TriangleDownIcon boxSize={3} color="blue.500" />
    );
  };

  // Detect if we're on mobile (base) or desktop (md and above)
  // Must be called before any early returns to maintain hook order
  const isMobile = useBreakpointValue({ base: true, md: false });

  const SortableHeader = ({
    field,
    children,
    ...props
  }: {
    field: SortField;
    children: React.ReactNode;
    [key: string]: unknown;
  }) => (
    <Th
      cursor="pointer"
      onClick={() => handleSort(field)}
      _hover={{ bg: 'gray.100' }}
      whiteSpace="nowrap"
      {...props}>
      <HStack spacing={1}>
        <Text fontSize="xs">{children}</Text>
        {getSortIcon(field)}
      </HStack>
    </Th>
  );

  if (isLoading) {
    return (
      <Box>
        {Array.from({ length: 5 }).map((_, index) => (
          <Skeleton key={index} height="60px" mb={2} />
        ))}
      </Box>
    );
  }

  if (!transactions || transactions.length === 0) {
    return (
      <Alert status="info" borderRadius="md">
        <AlertIcon />
        {t('No transactions found for this period')}
      </Alert>
    );
  }

  // Get row background color based on flow
  const getRowBgColor = (isInflow: boolean) => {
    return showFlowBackgroundColors
      ? isInflow
        ? 'green.50'
        : 'red.50'
      : 'transparent';
  };

  // Get hover background color based on flow
  const getRowHoverBgColor = (isInflow: boolean) => {
    if (!showFlowBackgroundColors) return 'gray.50';
    return isInflow ? 'green.100' : 'red.100';
  };

  return (
    <Box>
      {/* Filter Panel */}
      {showFilters && (
        <TransactionFilterPanel
          transactions={transactions}
          filters={filters}
          onFiltersChange={setFilters}
          disableOwnerFilter={disableOwnerFilter}
          selectedOwnerId={selectedOwnerId}
        />
      )}

      {/* Results count */}
      <Text fontSize="xs" color="gray.500" mb={2}>
        {t('Showing {{shown}} of {{count}} transactions', {
          shown: filteredTransactions.length,
          count: transactions.length,
        })}
      </Text>

      {filteredTransactions.length === 0 ? (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          {t('No transactions match the current filters')}
        </Alert>
      ) : tableView ? (
        <TableContainer
          overflowX="auto"
          maxH={maxHeight}
          overflowY={maxHeight ? 'auto' : undefined}>
          <Table variant="simple" size="sm">
            <Thead
              bg="gray.50"
              position={maxHeight ? 'sticky' : undefined}
              top={0}
              zIndex={1}>
              <Tr>
                <SortableHeader field="created_at">{t('Date')}</SortableHeader>
                {!isMobile && (
                  <SortableHeader field="flow">{t('Flow')}</SortableHeader>
                )}
                <SortableHeader field="category">
                  {t('Category')}
                </SortableHeader>
                <SortableHeader field="amount">{t('Amount')}</SortableHeader>
                <SortableHeader field="payment_type">
                  {t('Payment')}
                </SortableHeader>
                {!isMobile && (
                  <SortableHeader field="origin_description">
                    {t('Origin')}
                  </SortableHeader>
                )}
                {effectiveReverseHandler && (
                  <Th whiteSpace="nowrap">
                    <Text fontSize="xs">{t('Actions')}</Text>
                  </Th>
                )}
              </Tr>
            </Thead>
            <Tbody>
              {(() => {
                const renderRow = (
                  transaction: AccountTransaction,
                  partnerInfo?: {
                    tx: AccountTransaction;
                    relationship: ReversalRelationship;
                  },
                ) => {
                  const partner = partnerInfo?.tx;
                  const relationship = partnerInfo?.relationship;
                  const paired = !!partner;
                  const hasComments =
                    transaction.comments && transaction.comments.length > 0;
                  const isInflow = transaction.flow === 'INFLOW';
                  const isReversal = isReversalTransaction(transaction);
                  const isReversed = isReversedTransaction(transaction);
                  // The item keeps its natural styling; the nested partner
                  // preview inside this row is lower hierarchy.
                  const isMuted = !paired && (isReversal || isReversed);
                  const canReverse =
                    !!effectiveReverseHandler &&
                    isReversibleTransaction(transaction);
                  const strikeProps = isMuted ? { color: 'gray.500' } : {};
                  // Shared lower-hierarchy styling for nested partner lines.
                  const partnerTextProps = {
                    fontSize: '2xs' as const,
                    color: 'gray.500',
                    fontStyle: 'italic' as const,
                  };

                  return (
                    <Tr
                      key={txScopedKey(transaction)}
                      onClick={() => onTransactionClick?.(transaction)}
                      cursor={onTransactionClick ? 'pointer' : 'default'}
                      bg={paired ? PAIR_GROUP_BG : getRowBgColor(isInflow)}
                      opacity={isMuted ? 0.75 : 1}
                      _hover={{
                        bg: paired
                          ? 'purple.100'
                          : getRowHoverBgColor(isInflow),
                      }}
                      transition="background 0.2s">
                      {/* Date - Responsive */}
                      <Td py={2} px={{ base: 1, md: 2 }}>
                        <VStack align="start" spacing={0}>
                          <HStack spacing={1}>
                            <Text
                              fontSize={{ base: 'xs', md: 'xs' }}
                              fontWeight="medium"
                              {...strikeProps}>
                              {moment(transaction.created_at).format(
                                'DD.MM.YY @ HH:mm',
                              )}
                            </Text>
                          </HStack>
                          <Text
                            display={
                              transaction.creator?.id?.toLowerCase() ===
                              transaction.owner?.id?.toLowerCase()
                                ? 'none'
                                : 'block'
                            }
                            fontSize="xx-small"
                            color="gray.500">
                            {t('Creator: {{name}}', {
                              name: transaction.creator?.name,
                            })}
                          </Text>
                          <Text
                            display={
                              transaction.creator?.id?.toLowerCase() ===
                              transaction.owner?.id?.toLowerCase()
                                ? 'none'
                                : 'block'
                            }
                            fontSize="xx-small"
                            color="gray.500">
                            {t('Owner: {{name}}', {
                              name: transaction.owner?.name,
                            })}
                          </Text>
                          <Text
                            display={
                              transaction.creator?.id?.toLowerCase() ===
                              transaction.owner?.id?.toLowerCase()
                                ? 'block'
                                : 'none'
                            }
                            fontSize="xx-small"
                            color="gray.500">
                            {t('Creator/Owner: {{name}}', {
                              name: transaction.owner?.name,
                            })}
                          </Text>
                          {partner && relationship && (
                            <Box
                              mt={1}
                              pt={1}
                              alignSelf="stretch"
                              borderTopWidth="1px"
                              borderTopStyle="dashed"
                              borderTopColor="purple.300">
                              <Text {...partnerTextProps}>
                                {moment(partner.created_at).format(
                                  'DD.MM.YY @ HH:mm',
                                )}{' '}
                                ·{' '}
                                {partner.creator?.name ||
                                  partner.owner?.name ||
                                  t('Unknown')}
                              </Text>
                            </Box>
                          )}
                        </VStack>
                      </Td>
                      {/* Flow - Desktop only */}
                      {!isMobile && (
                        <Td py={2} px={{ base: 1, md: 2 }}>
                          <VStack align="start" spacing={0.5}>
                            <Badge
                              colorScheme={isInflow ? 'green' : 'red'}
                              fontSize="xs"
                              px={1}
                              opacity={isMuted ? 0.6 : 1}
                              aria-label={
                                isInflow
                                  ? t('Inflow transaction')
                                  : t('Outflow transaction')
                              }>
                              {isInflow ? t('IN') : t('OUT')}
                            </Badge>
                            {partner && (
                              <Badge
                                colorScheme={
                                  partner.flow === 'INFLOW' ? 'green' : 'red'
                                }
                                variant="outline"
                                fontSize="2xs"
                                opacity={0.7}>
                                {partner.flow === 'INFLOW' ? t('IN') : t('OUT')}
                              </Badge>
                            )}
                          </VStack>
                        </Td>
                      )}
                      {/* Category */}
                      <Td
                        py={2}
                        px={{ base: 1, md: 2 }}
                        maxW={{ base: '80px', md: '120px', lg: '180px' }}
                        whiteSpace="normal"
                        wordBreak="break-word">
                        <VStack align="start" spacing={0}>
                          <Text
                            fontSize={{ base: 'xs', md: 'sm' }}
                            {...strikeProps}>
                            {getTransactionCategoryLabel(transaction.category)}
                          </Text>
                          {partner && (
                            <Text {...partnerTextProps}>
                              {getTransactionCategoryLabel(partner.category)}
                            </Text>
                          )}
                          {!paired && isReversal && (
                            <Badge
                              colorScheme="orange"
                              variant="subtle"
                              fontSize="2xs">
                              {t('Cancellation')}
                            </Badge>
                          )}
                          {!paired && isReversed && !isReversal && (
                            <Badge
                              colorScheme="gray"
                              variant="subtle"
                              fontSize="2xs">
                              {t('Cancelled')}
                            </Badge>
                          )}
                          {hasComments && (
                            <Icon as={FaComment} color="gray.400" boxSize={3} />
                          )}
                        </VStack>
                      </Td>
                      {/* Amount */}
                      <Td py={2} px={{ base: 1, md: 2 }} textAlign="center">
                        <VStack align="center" spacing={0}>
                          <Text
                            fontSize={{ base: 'xs', md: 'sm' }}
                            fontWeight="bold"
                            color={
                              isMuted
                                ? 'gray.500'
                                : isInflow
                                  ? 'green.600'
                                  : 'red.600'
                            }
                            textDecoration={
                              isReversed && !isReversal
                                ? 'line-through'
                                : undefined
                            }
                            whiteSpace="nowrap">
                            {formatCurrency(transaction.amount, true)}
                          </Text>
                          {partner && (
                            <Text {...partnerTextProps} whiteSpace="nowrap">
                              {formatCurrency(partner.amount, true)}
                            </Text>
                          )}
                        </VStack>
                      </Td>
                      {/* Payment Type */}
                      <Td
                        py={2}
                        px={{ base: 1, md: 2 }}
                        maxW={{ base: '70px', md: '100px', lg: '140px' }}
                        whiteSpace="normal"
                        wordBreak="break-word">
                        <VStack align="start" spacing={0}>
                          <Text
                            fontSize={{ base: 'xs', md: 'sm' }}
                            {...strikeProps}>
                            {getPaymentMethodTypeLabel(
                              transaction.payment_type,
                            )}
                          </Text>
                          {partner && (
                            <Text {...partnerTextProps}>
                              {getPaymentMethodTypeLabel(partner.payment_type)}
                            </Text>
                          )}
                        </VStack>
                      </Td>
                      {/* Origin Description - Desktop only */}
                      {!isMobile && (
                        <Td
                          py={2}
                          px={{ base: 1, md: 2 }}
                          maxW={{ md: '120px', lg: '180px' }}
                          whiteSpace="normal"
                          wordBreak="break-word">
                          <Text
                            fontSize="xs"
                            color={isMuted ? 'gray.500' : 'gray.600'}>
                            {transaction.origin_entity?.description || '—'}
                          </Text>
                        </Td>
                      )}
                      {/* Actions */}
                      {effectiveReverseHandler && (
                        <Td py={2} px={{ base: 1, md: 2 }}>
                          {canReverse ? (
                            <Tooltip
                              label={t('Delete Transaction')}
                              placement="top">
                              <IconButton
                                aria-label={t('Delete Transaction')}
                                icon={<Icon as={FiRotateCcw} />}
                                size="xs"
                                variant="ghost"
                                colorScheme="orange"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  effectiveReverseHandler(transaction);
                                }}
                              />
                            </Tooltip>
                          ) : null}
                        </Td>
                      )}
                    </Tr>
                  );
                };

                return sortedTransactions.map((transaction) => {
                  const partner = getTransactionPartner(
                    transaction,
                    partnerLookup,
                  );
                  const relationship = partner
                    ? getReversalRelationship(transaction, partner)
                    : undefined;

                  // Exactly one <Tr> per authoritative item — the partner is
                  // rendered as nested lower-hierarchy details inside it.
                  return renderRow(
                    transaction,
                    partner && relationship
                      ? { tx: partner, relationship }
                      : undefined,
                  );
                });
              })()}
            </Tbody>
          </Table>
        </TableContainer>
      ) : (
        <TransactionCardList
          transactions={sortedTransactions}
          onTransactionClick={onTransactionClick}
          getRowBgColor={getRowBgColor}
          getRowHoverBgColor={getRowHoverBgColor}
          isCompact={isCompact}
          onReverseTransaction={effectiveReverseHandler}
          partnerLookup={partnerLookup}
        />
      )}
      {reverseEnabled && !onReverseTransaction && internalReverseModal}
    </Box>
  );
};
